/* eslint-disable @typescript-eslint/no-explicit-any */


import * as L from 'leaflet';
import * as G from 'geojson';
import Typography from '@mui/material/Typography';
import { PaintLineString, PaintPolygon } from '../mapa';

export interface PolyZssData {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any;
}

type LatLngExpression = L.LatLngExpression;

export class PolyZss extends L.Layer {
  // Propriedades de dados/estilo
  public properties?: PolyZssData;
  public paintOptions: PaintLineString;

  // interno: Polygon OU LayerGroup<Polygon>
  private inner: | L.Polygon<PolyZssData> | L.LayerGroup<L.Polygon<PolyZssData>>;
  private map?: L.Map;

  constructor(
    geometries:
      | LatLngExpression[]              // 1 ring
      | LatLngExpression[][]            // polygon com holes
      | LatLngExpression[][][],         // vários polígonos -> LayerGroup
    properties?: PolyZssData,
    paintOptions?: PaintPolygon
  ) {
    super();

    this.properties = properties;
    this.paintOptions = paintOptions ?? {
      color: 'rgba(255, 255, 0, 1)',
      weight: 2,
    };

    // Decidir se é Polygon único ou grupo de Polygons
    const isGroup =
      Array.isArray(geometries) &&
      Array.isArray((geometries as any)[0]) &&
      Array.isArray((geometries as any)[0][0]);

    if (isGroup) { // vários polígonos
      const polys = (geometries as LatLngExpression[][][]).map((coords) => L.polygon<PolyZssData>(coords, this.paintOptions));
      this.inner = L.layerGroup<L.Polygon<PolyZssData>>(polys);

      // Eventos por polígono
      this.forEachPolygon((p) => {
        p.on({
          mouseover: this.highlightFeature,
          mouseout: this.resetStyle,
          click: this.clickFeature,
        });

        if (this.paintOptions.bindPopUp) {
          p.bindPopup(this.popUpComponent(), {
            className: '',
            maxWidth: 390,
          });
        }
        if (this.paintOptions.bindTooltip) {
          p.bindTooltip(this.tooltipComponent(), { direction: 'top' });
        }
      });
    } else {

      this.inner = L.polygon<PolyZssData>(geometries as any, this.paintOptions);

      this.inner.on({
        mouseover: this.highlightFeature,
        mouseout: this.resetStyle,
        click: this.clickFeature,
      });

      if (this.paintOptions.bindPopUp) {
        this.inner.bindPopup(this.popUpComponent(), {
          className: '',
          maxWidth: 390,
        });
      }
      if (this.paintOptions.bindTooltip) {
        this.inner.bindTooltip(this.tooltipComponent(), { direction: 'top' });
      }
    }
  }

  private isPolygon(x?: L.Polygon<PolyZssData> | L.LayerGroup<L.Polygon<PolyZssData>>): x is L.Polygon<PolyZssData> {
    return (x ?? this.inner) instanceof L.Polygon;
  }

  private forEachPolygon(cb: (p: L.Polygon<PolyZssData>) => void) {
    if (this.isPolygon(this.inner)) {
      cb(this.inner);
    } else {
      this.inner.eachLayer((layer) => {
        if (layer instanceof L.Polygon) cb(layer);
      });
    }
  }

  // obrigatório
  onAdd(map: L.Map): this {
    this.map = map;
    this.inner.addTo(map);
    return this;
  }

  // obrigatório
  onRemove(map: L.Map): this {
    this.inner.removeFrom(map);
    return this;
  }

  /** Adiciona ao mapa */
  addTo(map: L.Map): this {
    this.inner.addTo(map);
    return this;
  }

  /** Remove do mapa */
  remove(): this {
    this.inner.remove();
    return this;
  }

  /** Zas para Geojson */
  toGeoJSON(precision?: number | false) {
    if (this.isPolygon()) {
      return {
        type: "Feature",
        geometry: (this.inner.toGeoJSON(precision) as G.Feature<G.Polygon | G.MultiPolygon, PolyZssData>).geometry,
        properties: this.properties,
      } as G.Feature<G.Polygon, PolyZssData | undefined>;
    }

    const inData = this.inner as L.LayerGroup<L.Polygon<PolyZssData>>;

    return {
      type: "Feature",
      geometry: {
        type: "MultiPolygon",
        coordinates: (inData.getLayers() as L.Polygon<PolyZssData>[]).map(layer => {
          return (layer.toGeoJSON(precision).geometry as G.Polygon).coordinates;
        }),
      } as G.MultiPolygon,
      properties: this.properties,
    } as G.Feature<G.MultiPolygon, PolyZssData | undefined>;
  }

  /** Bounds do(s) polígono(s) */
  getBounds(): L.LatLngBounds {
    if (this.isPolygon(this.inner)) {
      return this.inner.getBounds();
    }
    let bounds: L.LatLngBounds | null = null;
    this.forEachPolygon((p) => {
      const b = p.getBounds();
      bounds = bounds ? bounds.extend(b) : L.latLngBounds(b.getSouthWest(), b.getNorthEast());
    });
    return bounds ?? L.latLngBounds([]);
  }

  /** Estilo (aplica a todos os polígonos no grupo) */
  setStyle(style?: L.PathOptions): this {
    if (!style) return this;
    this.forEachPolygon((p) => p.setStyle(style));
    return this;
  }

  /** Bind de popup em todos os polígonos */
  bindPopup(
    content: L.Popup | string | HTMLElement,
    opts?: L.PopupOptions
  ): this {
    this.forEachPolygon((p) => p.bindPopup(content as any, opts));
    return this;
  }

  /** Bind de tooltip em todos os polígonos */
  bindTooltip(
    content: L.Tooltip | string | HTMLElement,
    opts?: L.TooltipOptions
  ): this {
    this.forEachPolygon((p) => p.bindTooltip(content as any, opts));
    return this;
  }

  /** Clone Layer */
  clone() {
    const geometries: L.LatLngExpression[][][] = [];

    if (this.inner instanceof L.Polygon) {
      const coords = this.inner.getLatLngs();
      geometries.push(coords as L.LatLngExpression[][]);
    } else {
      this.inner.eachLayer((layer) => {
        if (layer instanceof L.Polygon) {
          geometries.push(layer.getLatLngs() as L.LatLngExpression[][]);
        }
      });
    }

    return new PolyZss(
      geometries.length === 1 ? geometries[0] : geometries,
      this.properties ? { ...this.properties } : undefined,
      this.paintOptions ? { ...this.paintOptions } : undefined
    );
  }

  /** Obtendo as LatLons */
  getLatLngs(): L.LatLngExpression[][] | L.LatLngExpression[][][] {
    if (this.inner instanceof L.Polygon) {
      return this.inner.getLatLngs() as L.LatLngExpression[][];
    }

    const geometries: L.LatLngExpression[][][] = [];

    this.inner.eachLayer((layer) => {
      if (layer instanceof L.Polygon) {
        geometries.push(layer.getLatLngs() as L.LatLngExpression[][]);
      }
    });

    return geometries;
  }

  /** Getter de bbox como propriedade calculada */
  get bbox(): L.LatLngBounds {
    return this.getBounds();
  }

  /** Acesso ao objeto interno, se necessário */
  get raw() {
    return this.inner;
  }

  private resetStyle = () => this.setStyle(this.paintOptions);

  private highlightFeature = () => {
    if (this.paintOptions.paintHovered) {
      this.setStyle(this.paintOptions.paintHovered);
    }
  };

  private clickFeature = () => {
    if (this.paintOptions.paintClick) {
      this.setStyle(this.paintOptions.paintClick);
    }
  };

  public popUpComponent() {
    return window.JSXToHTML(
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          justifyItems: 'center',
          height: '10em',
          width: '19em',
          overflowY: 'auto',
        }}
      ></div>
    );
  }

  public tooltipComponent() {
    return window.JSXToHTML(
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          justifyItems: 'center',
          overflowY: 'auto',
        }}
      >
        <Typography>{this.properties?.name ?? 'ZSS'}</Typography>
      </div>
    );
  }
}
